// React dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory, Route, Link as RouterLink, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';

// Material UI dependencies
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    ErrorOutline as ErrorOutlineIcon,
    Close as CloseIcon,
} from '@material-ui/icons';
import {
    AppBar,
    Avatar,
    Button,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    CircularProgress,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';

// SVG images
import ClientsIcon from '../../assets/icons/clients.svg';
import InventoryIcon from '../../assets/icons/inventory.svg';
import CollectionIcon from '../../assets/icons/collections.svg';
import ConfigIcon from '../../assets/icons/config.svg';
import EditIcon from '../../assets/icons/edit.svg';
import HomeIcon from '../../assets/icons/home.svg';
import logo from '../../assets/img/logo.svg';
import { BellNotification } from '../../assets/icons/notificationIcon.js';
import OrdersIcon from '../../assets/icons/orders.svg';
import OvaloIcon from '../../assets/icons/ovalo.svg';
import ProductIcon from '../../assets/icons/products.svg';
import CouponIcon from '../../assets/icons/coupon.svg';
import openInNewIcon from '../../assets/icons/open-in-new.svg';
import WPIcon from '../../assets/icons/whatsapp.svg';
// Internal components
import ActionCategorieMobile from '../../pages/ActionCategorieMobile/ActionCategorieMobile';
import AddPaymentProvidersPage from '../../pages/AddPaymentProvidersPage/AddPaymentProvidersPage';
import BillDetailPage from '../../pages/BillDetailPage/BillDetailPage';
import BillingPage from '../../pages/BillingPage/BillingPage';
import BillPaymentPage from '../../pages/BillPaymentPage/BillPaymentPage';
import ClientsPage from '../../pages/ClientsPage/ClientsPage';
import ClientsPageMobile from '../../pages/ClientsPage/ClientsPageMobile';
import Collections from '../../pages/CollectionsPage/CollectionsPage';
import ConfigPage from '../../pages/ConfigPage/ConfigPage';
import CreateOrder from '../../pages/CreateOrder/CreateOrder';
import DomainPage from '../../pages/DomainPage/DomainPage';
import FooterMenuBar from '../../components/MobileUX/FooterBar/FooterBar';
import FormPage from '../../pages/FormPage/FormPage';
import GeneralPage from '../../pages/GeneralPage/GeneralPage';
import HomePage from '../../pages/HomePage/HomePage';
import HomePageMobile from '../../pages/HomePageMobile/HomePageMobile';
import InventoryPage from '../../pages/InventoryPage/InventoryPage';
import InventoryImportPage from '../../pages/InventoryImportPage/InventoryImportPage';
import ImportProductsPage from '../../pages/ImportProductsPage/ImportProductsPage';
import LegalPage from '../../pages/LegalPage/LegalPage';
import ManageCollection from '../../components/ManageCollection/ManageCollection';
import ManageProductPage from '../../pages/ManageProductPage/ManageProductPage';
import ManageStorePage from '../../pages/ManageStorePage/ManageStorePage';
import ManageUserRole from '../../pages/ManageUserRole/ManageUserRole';
import ManageUserStorePage from '../../pages/ManageUserStorePage/ManageUserStorePage';
import MassiveUploadTable from '../../pages/MassiveUploadTable/MassiveUploadTable';
import MobileDrawer from '../../components/MobileDrawer/MobileDrawer';
import OrderInfoPage from '../../pages/OrderInfoPage/OrderInfoPage';
import OrdersTablePage from '../../pages/OrdersTablePage/OrdersTablePage';
import PaymentPage from '../../pages/PaymentPage/PaymentPage';
import PaymentProvidersPage from '../../pages/PaymentProvidersPage/PaymentProvidersPage';

import PreviewLinkStore from '../../components/PreviewLinkStore/PreviewLinkStore';
import ProductsTablePage from '../../pages/ProductsTablePage/ProductsTablePage';
import QrPage from '../../pages/QrPage/QrPage';
import ThankUPage from '../../pages/ThankUPage/ThankUPage';
import ShippingConfigPage from '../../pages/ShippingConfigPage/ShippingConfigPage';

import UsersRolesPage from '../../pages/UsersRolesPage/UsersRolesPage';
import UserStoresTablePage from '../../pages/UserStoresTablePage/UserStoresTablePage';
import WhatsAppWidget from '../../components/WhatsAppWidget/WhatsAppWidget';
import WizardSelectPlan from '../../components/BillingPage/WizardSelectPlan/WizardSelectPlan';
import ClientInfoPage from '../../pages/ClientInfoPage/ClientInfoPage';
import ClientInfoPageMobile from '../../pages/ClientInfoPage/ClientInfoPageMobile';

import { Mixpanel } from '../../constants.js/mixpanel';
import { clarityIdentify } from '../../utils/clarity';

// Internal dependencies
import { getStoreService } from '../../services/store';
// import { getStoreUsageService } from '../../services/billing';
import { getNotificationsStatic } from '../../services/notifications';
import { logOut } from '../../modules/auth';
import { setStoreData } from '../../modules/common';
import { setIsPlanNearExpiry } from '../../modules/user-stores.js';
import { routes } from '../../constants.js/misc';
import { useWelcomeMessage } from '../../hook/use-welcome-message';

// Styles
import {
    drawerStyles,
    UserMenu,
    TopBar,
    FooterBar,
    ContentItems,
    Actions,
    IconOvalo,
    InfoNotification,
    NotificationContainer,
    ContainerFreePlanAlert,
} from './AppContainer.styles';
import DownloadReportPage from '../../pages/DownloadReportPage/DownloadReportPage.js';
import PopupFinishTrialPlan from '../../components/BillingPage/PopupFinishTrialPlan/PopupFinishTrialPlan.js';
import SubcategoriesFeatureVideoModal from '../../components/SubcategoriesFeatureVideoModal/SubcategoriesFeatureVideoModal';
import {
    hasSeenSubcategoriesVideo,
    markSubcategoriesVideoSeen,
} from '../../constants/subcategoriesFeatureVideo';
import { getProductsService, isFreemiumPlanAndProductsReachedLimit } from '../../services/products.js';
import AddProductModal from '../../components/Popups/add-product-modal.js';
import { parseDataFromCookie } from '../../utils/cookie-utils.js';
import { colors } from '../../constants.js/colors.js';
import EditStore from '../../pages/EditStore/EditStore.js';
import { FuncShareIcon } from '../../assets/icons/funcShareIcon.js';
import HomePageMobileNewUsers from '../../pages/HomePage/HomePageMobileNewUsers.js';
import OrderPageMobile from '../../pages/OrderPageMobile/OrderPageMobile.js';
import OrderInfoPageMobile from '../../pages/OrderInfoPageMobile/OrderInfoPageMobile.js';
import { formatDateAndMonth } from '../../utils/date-formatter.js';
import MobileUserStoresTablePage from '../../components/MobileUserStoresTablePage/index.js';
import MobileInventoryPage from '../../pages/InventoryPage/MobileInventoryPage.js';
import ProductsMobilePage from '../../pages/ProductsTablePage/ProductsTablePageMobile.js';
import CouponsPage from '../../pages/CouponsPage/CouponsPage.js';
import PromotionsPage from '../../pages/PromotionsPage/PromotionsPage.js';
import PromotionFormPage from '../../pages/PromotionsPage/PromotionFormPage.js';
import CartDiscountTierFormPage from '../../pages/PromotionsPage/CartDiscountTierFormPage.js';
import EcometriPlusPage from '../../pages/EcometriPlusPage/EcometriPlusPage.js';
import { CART_SUBTOTAL_FEATURE_NAME, STOREFRONT_PROMOTIONS_ENABLED, isCouponsFeatureEnabled, isPromotionsFeatureEnabled } from '../../constants/promotionConfig';
import ButtonUpgradePlan from '../../components/ButtonUpgradePlan';
import FreemiumCouponNavBadge from '../../components/FreemiumCouponNavBadge/FreemiumCouponNavBadge';
import BetaNavBadge from '../../components/BetaNavBadge/BetaNavBadge';

const AppContainer = ({
    storeId,
    isLoggingOut,
    logOut,
    userEmail,
    userType,
    setStoreData,
    storeData,
    showWelcomePopup,
    setIsPlanNearExpiry,
    auth,
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const classes = drawerStyles();

    // State variables
    const [anchorEl, setAnchorEl] = useState(null);
    const [collectionIsActive, setCollectionIsActive] = useState(false);
    const [storeDefault, setStoreDefault] = useState({});
    const [notificationMessageStatic, setnotificationMessageStatic] = useState('');
    const [storeNameDefault, setNameStoreDefault] = useState('');
    const [open, setOpen] = useState(true);
    const [hasSubcategoriesBellDot, setHasSubcategoriesBellDot] = useState(false);
    const [ordersIsActive, setOrdersIsActive] = useState(false);
    const [inventoryIsActive, setInventoryIsActive] = useState(false);
    const [clientIsActive, setClientIsActive] = useState(false);
    const [configIsActive, setConfigIsActive] = useState(false);
    const [homeIsActive, setHomeIsActive] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [productsIsActive, setProductsIsActive] = useState(false);
    const [couponsIsActive, setCouponsIsActive] = useState(false);
    const [promotionsIsActive, setPromotionsIsActive] = useState(false);
    const [ecometriPlusIsActive, setEcometriPlusIsActive] = useState(false);
    const [title, setTitle] = useState(false);
    const [infoStore, setInfoStore] = useState('');
    const [selectPlanDialogOpen, setSelectPlanDialogOpen] = useState(false);
    const [showPlanDialogOpen, setShowPlanDialogOpen] = useState(false);
    const [wsIsActive, setWsIsActive] = useState(false);
    const storedDate = storeData?.store_plan?.trial_ended_at;
    const storedDateObj = new Date(storedDate);
    const currentDateObj = new Date();
    const showBannerFreeTrial = currentDateObj <= storedDateObj;
    const { showPopup, setShowPopup } = useWelcomeMessage();
    const [openWelcomePopup, setOpenWelcomePopup] = useState(showPopup);
    const [openAddProductModal, setOpenAddProductModal] = useState(false);
    const [isStoreHasProduct, setIsStoreHasProduct] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isPlanReminderShown, setIsPlanReminderShown] = useState(false);
    const [nextCutDate, setNextCutDate] = useState('');
    const [showFreemiumProductsModal, setShowFreemiumProductsModal] = useState(false);
    const [showSubcategoriesVideoModal, setShowSubcategoriesVideoModal] = useState(false);

    const isFreemiumPlan =
        storeData?.store_plan?.plan?.name &&
        String(storeData.store_plan.plan.name).toLowerCase() === 'freemium';

    const promotionsFeatureEnabled = isPromotionsFeatureEnabled(storeData);
    const couponsFeatureEnabled = isCouponsFeatureEnabled(storeData);

    const routeTitles = {
        [routes.addPaymentProviders]: t('Config.paymentProviders'),
        [routes.collectionsPage]: t('CollectionsTitle'),
        [routes.config]: t('ConfigTitle'),
        [routes.createCollection]: t('CollectionsTitle'),
        [routes.createProduct]: t('ProductsTitle'),
        [routes.createUserRole]: t('UserRole.route'),
        [routes.createUserStore]: t('UserStoresTitle'),
        [routes.createOrder]: t('OrderTitle'),
        [routes.domain]: t('Config.domain'),
        [routes.form]: t('Config.form'),
        [routes.general]: t('Config.generalRoute'),
        [routes.homeUrl]: t('HomePage'),
        [routes.importProducts]: t('ImportProducts.titleRoute'),
        [routes.legal]: t('Config.legalRoute'),
        [routes.ordersPage]: t('OrdersTitle'),
        [routes.inventoryPage]: t('InventoryTitle'),
        [routes.inventoryImportPage]: t('InventoryImportTitle'),
        [routes.clientPage]: t('ClientTitle'),
        [routes.paymentProviders]: t('Config.paymentProviders'),
        [routes.productsTable]: t('ProductsTitle'),
        [routes.qrCode]: t('Config.qrCode'),
        [routes.shipping]: t('Config.shippingRoute'),
        [routes.store]: t('wsTitleStore'),
        [routes.updateCollection]: t('CollectionsTitle'),
        [routes.updateProduct]: t('ProductsTitle'),
        [routes.updateUserRole]: t('UserRole.route'),
        [routes.updateUserStore]: t('UserStoresTitle'),
        [routes.upgradePaymentProviders]: t('Config.paymentProviders'),
        [routes.usersRoles]: t('UserRole.route'),
        [routes.userStores]: t('UserStoresTitle'),
    };

    routeTitles[routes.couponsPage] = t('CouponsTitle');
    routeTitles[routes.promotionsPage] = t('PromotionsTitle');
    routeTitles[routes.promotionsCreate] = t('Promotions.createTitle');
    routeTitles['/promotions/edit'] = t('Promotions.editTitle');
    routeTitles['/promotions/tier/edit'] = t('Promotions.editTierTitle');
    routeTitles[routes.promotionsCreateCartTier] = `Nuevo ${CART_SUBTOTAL_FEATURE_NAME}`;
    routeTitles[routes.promotionsCartTierEdit] = `Editar ${CART_SUBTOTAL_FEATURE_NAME}`;
    routeTitles[routes.ecometriPlusPage] = t('EcometriPlusTitle');

    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 600px)' });

    // Functions

    const toggleDrawer = () => {
        setOpen(!open);
        setMobileDrawerOpen(!mobileDrawerOpen);
    };

    const handleUserMenuClick = (event) => setAnchorEl(event.currentTarget);

    const handleClose = () => setAnchorEl(null);

    const logoutAndGoHome = () => {
        // New anonymous Mixpanel identity, so the next signup on this browser is not attributed
        // to this store. Only on an explicit logout (App also calls logOut() on anonymous loads).
        Mixpanel.reset();
        logOut();
        history.push('/');
        // unloadHubspotChat();
    };

    const currentPathName =
        history.location.pathname.slice(0, history.location.pathname.lastIndexOf('/')) ||
        history.location.pathname;

    const setTitles = useCallback(() => {
        const currentPathName =
            history.location.pathname.slice(0, history.location.pathname.lastIndexOf('/')) ||
            history.location.pathname;

        setTitle(routeTitles[history.location.pathname] || routeTitles[currentPathName]);

        setProductsIsActive(
            currentPathName === routes.productsTable ||
            currentPathName === routes.createProduct ||
            currentPathName === routes.editProduct
        );

        setCouponsIsActive(currentPathName === routes.couponsPage);

        setPromotionsIsActive(
            history.location.pathname === routes.promotionsPage ||
            history.location.pathname.startsWith('/promotions/')
        );

        setEcometriPlusIsActive(history.location.pathname === routes.ecometriPlusPage);

        setCollectionIsActive(
            currentPathName === routes.collectionsPage ||
            currentPathName === routes.updateCollection
        );
        setOrdersIsActive(currentPathName === routes.ordersPage);
        setInventoryIsActive(currentPathName === routes.inventoryPage);
        setClientIsActive(currentPathName === routes.clientPage);
        setHomeIsActive(
            history.location.pathname === routes.homeUrl ||
            history.location.pathname.replace(/\/$/, '') === routes.homeUrl
        );
        setConfigIsActive(currentPathName === routes.config);
        setWsIsActive(
            currentPathName === routes.store ||
            currentPathName === routes.userStores ||
            currentPathName === routes.createUserStore ||
            currentPathName === routes.updateUserStore
        );
    });

    /**FUNCION PARA CONFIRMAR EL ELMINAR */
    const viewLinkPreview = () => {
        setCancelDialogOpen(true);
    };

    const notionHelp = () =>
        window.open(
            'https://www.notion.so/get10x/Centro-de-ayuda-de-10X-8d198608195a4e5faaf23674348c5b12'
        );

    const goPageMenuBar = (value) => {
        switch (value) {
            case 'config':
                history.push(routes.config);
                break;
            default:
                break;
        }
    };

    const refreshSubcategoriesBellDot = useCallback(() => {
        if (!storeId) {
            setHasSubcategoriesBellDot(false);
            return;
        }
        setHasSubcategoriesBellDot(!hasSeenSubcategoriesVideo(storeId));
    }, [storeId]);

    const openSubcategoriesVideoFromBell = () => {
        if (storeId && hasSeenSubcategoriesVideo(storeId)) return;
        setShowSubcategoriesVideoModal(true);
    };

    const dismissSubcategoriesVideoModal = () => {
        if (storeId) {
            markSubcategoriesVideoSeen(storeId);
        }
        setShowSubcategoriesVideoModal(false);
        refreshSubcategoriesBellDot();
    };

    const renderSubcategoriesBell = () => (
        <Tooltip title="Novedades: subcategorías y precios mayoristas" placement="bottom">
            <NotificationContainer
                onClick={openSubcategoriesVideoFromBell}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openSubcategoriesVideoFromBell();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="Ver novedad sobre subcategorías"
            >
                <Badge color="secondary" variant="dot" invisible={!hasSubcategoriesBellDot}>
                    <BellNotification />
                </Badge>
            </NotificationContainer>
        </Tooltip>
    );

    const handleResize = () => {
        if (window.innerWidth < 1024) setOpen(false);
        if (window.innerWidth >= 1024) setOpen(true);
    };

    const onAddProductClick = () => {
        setOpenAddProductModal(false);
    };

    const renewStorePlan = () => {
        const message = encodeURIComponent(
            'Hola, vengo de mi tienda en Ecometri y me gustaría renovar mi plan'
        );
        window.open(`https://wa.me/17543296020?text=${message}`, '_blank', 'noopener,noreferrer');
    };

    // EFFECTS ⬇️
    useEffect(() => {
        //
        if (!storeNameDefault) {
            getStoreService(storeId)
                .then(({ data }) => {
                    // No Mixpanel identify here: the panel sends no events, and an identify on every
                    // login could merge two stores' profiles (Original ID Merge). The store is linked
                    // once, at signup. If panel events are ever added back, identify here too: the
                    // wrapper resets first when the browser was identified as another store.
                    setStoreDefault(data);
                    setNameStoreDefault(data.name);
                    // Save store data in redux
                    setStoreData(data);
                })
                .catch((err) => {
                    throw new Error(`Error fetching store data: ${err.message}`);
                });
        }
    }, [storeNameDefault, storeId, setStoreData]);

    history.listen(() => setTitles());

    useEffect(() => {
        setTitles();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [setTitles]);

    useEffect(() => {
        refreshSubcategoriesBellDot();
    }, [refreshSubcategoriesBellDot]);

    // Regular logins don't call Mixpanel.identify: tag the Clarity session with the store here.
    useEffect(() => {
        if (storeId) clarityIdentify(storeId);
    }, [storeId]);

    useEffect(() => {
        if (!storeId) return;
        if (hasSeenSubcategoriesVideo(storeId)) return;
        setShowSubcategoriesVideoModal(true);
    }, [storeId]);

    useEffect(() => {
        // Disabled: GET `/stores/${storeId}/billing/.../usage`
        // if (!infoStore) {
        //     getStoreUsageService(storeId, 'CO')
        //         .then(({ data }) => {
        //             setInfoStore(data);
        //         })
        //         .catch((err) => { });
        // }
    }, [storeData, infoStore, storeId]);

    useEffect(() => {
        if (!storeData) return;

        if (storeId) {
            getProductsService(storeId, 1, 0)
                .then((res) => {
                    const totalProducts =
                        Number(res.headers['x-total-count']) ||
                        (Array.isArray(res.data) ? res.data.length : 0);
                    setIsStoreHasProduct(totalProducts > 0);
                })
                .catch(() => setIsStoreHasProduct(false));
        }

        if (storeData?.store_plan) {
                const { store_plan } = storeData;
                setNextCutDate(store_plan.next_cut_date);
                const isTrialEnded =
                    store_plan.store_plan_status_id === 3 && !store_plan.next_cut_date;
                const isNextCutDatePast =
                    store_plan.next_cut_date && new Date(store_plan.next_cut_date) < new Date();

                // Show dialog if trial ended or next cut date is past
                if (isTrialEnded || isNextCutDatePast) {
                    setShowPlanDialogOpen(true);
                }

                // Show dialog if next cut date is in 10 days or less
                if (store_plan.next_cut_date) {
                    const now = new Date();
                    const nextCutDate = new Date(store_plan.next_cut_date);
                    const diffTime = nextCutDate - now;
                    const diffDays = diffTime / (1000 * 60 * 60 * 24);
                    if (diffDays <= 15 && diffDays >= 0) {
                        setIsPlanReminderShown(true);
                        setIsPlanNearExpiry(true);
                    }
                }
            }
    }, [storeData, storeId]);

    useEffect(() => {
        if (!storeId) return;
        setLoading(true);
        isFreemiumPlanAndProductsReachedLimit(storeId, 20)
            .then((limitReached) => {
                if (limitReached) {
                    const sessionKey = `freemium-products-modal-shown-${storeId}`;
                    const hasSeenModal = sessionStorage.getItem(sessionKey);
                    if (!hasSeenModal) {
                        sessionStorage.setItem(sessionKey, '1');
                        setShowFreemiumProductsModal(true);
                    }
                    sessionStorage.setItem('freemium-block-add-products', '1');
                } else {
                    sessionStorage.removeItem('freemium-block-add-products');
                }
            })
            .finally(() => setLoading(false));
    }, [storeId]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Cargando...
                </Typography>
            </Box>
        );
    }

    return (
        <div className={classes.root}>
            <CssBaseline />

            {isPlanReminderShown && (
                <div
                    style={{
                        display: 'flex',
                        flex: '1',
                        justifyContent: 'center',
                        position: 'fixed',
                        top: 0,
                        width: '100%',
                        zIndex: 10,
                    }}
                >
                    <button className={classes.renewPlanButton} onClick={renewStorePlan}>
                        Tu plan expira el {formatDateAndMonth(nextCutDate)}. 👉 Renovar ahora
                    </button>
                </div>
            )}
            <AppBar
                elevation={1}
                position="fixed"
                className={clsx(classes.appBar, {
                    [classes.appBarShift]: open && !isTabletOrMobile,
                    [classes.lowerBar]: isPlanReminderShown,
                })}
                style={{
                    paddingLeft: isTabletOrMobile || (open && !isTabletOrMobile) ? 0 : '64px',
                }}
            >
                <TopBar
                    color="transparent"
                    style={{ justifyContent: 'space-between', gap: '.5rem' }}
                >
                    <div className={clsx(classes.headerActions)}>
                        <>
                            {isTabletOrMobile ? (
                                <span
                                    className={clsx(classes.menuButton)}
                                    style={{
                                        marginRight: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Avatar className={classes.avatarUSer} variant="circle">
                                        {userEmail ? userEmail.slice(0, 2) : '?'}
                                    </Avatar>
                                </span>
                            ) : (
                                <IconButton
                                    color="inherit"
                                    aria-label="open drawer"
                                    onClick={toggleDrawer}
                                    edge="start"
                                    style={{ marginRight: 16 }}
                                    className={clsx(classes.menuButton)}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                            {!isTabletOrMobile && (
                                <Typography
                                    color="secondary"
                                    variant={isTabletOrMobile ? 'body' : 'h6'}
                                    noWrap
                                >
                                    {title}
                                </Typography>
                            )}
                        </>
                        {isTabletOrMobile && (
                            <div className={classes.headerActionsContainerMobile}>
                                <Button
                                    onClick={viewLinkPreview}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    endIcon={<FuncShareIcon />}
                                >
                                    Mi tienda
                                </Button>

                                {renderSubcategoriesBell()}
                            </div>
                        )}
                        {isTabletOrMobile && (
                            <img style={{ height: '32px' }} src={logo} alt="logo" />
                        )}
                    </div>
                    {!isTabletOrMobile && (
                        <Actions>
                            <Button
                                className="btnLink"
                                onClick={viewLinkPreview}
                                color="primary"
                                variant="contained"
                            >
                                {t('PreviewLink')}
                            </Button>

                            <div style={{ display: 'flex' }}>
                                {renderSubcategoriesBell()}
                                <Avatar className={classes.avatarUSer}>
                                    {userEmail.slice(0, 2)}
                                </Avatar>
                                <IconOvalo src={OvaloIcon}></IconOvalo>
                            </div>

                            <UserMenu
                                anchorEl={anchorEl}
                                aria-controls="menu"
                                aria-haspopup="true"
                                onClick={handleUserMenuClick}
                            >
                                <div className="email">
                                    <Typography variant="body2">{userEmail}</Typography>
                                    {userType === 1 && (
                                        <Typography variant="caption">
                                            <b>Administrador</b>
                                        </Typography>
                                    )}
                                </div>
                                <KeyboardArrowDownIcon />
                            </UserMenu>
                            <Menu
                                id="simple-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                style={{ top: 55 }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem onClick={() => goPageMenuBar('config')}>
                                    {t('MenuHeader.config')}
                                </MenuItem>
                                <MenuItem onClick={notionHelp}>{t('MenuHeader.help')}</MenuItem>
                                <MenuItem onClick={logoutAndGoHome}>{t('Logout')}</MenuItem>
                            </Menu>
                        </Actions>
                    )}
                </TopBar>
                {showBannerFreeTrial && (
                    <ContainerFreePlanAlert>{/* <AlertPaymentPlan /> */}</ContainerFreePlanAlert>
                )}
            </AppBar>
            {isTabletOrMobile ? (
                <MobileDrawer
                    open={mobileDrawerOpen}
                    onClose={() => setMobileDrawerOpen(false)}
                    logOut={logoutAndGoHome}
                    dataStore={storeDefault}
                />
            ) : (
                <Drawer
                    variant="permanent"
                    anchor="left"
                    open={open}
                    className={clsx(classes.drawer, {
                        [classes.drawerOpen]: open,
                        [classes.drawerClose]: !open,
                    })}
                    classes={{
                        paper: clsx({
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        }),
                    }}
                >
                    <div className={classes.drawerHeader}>
                        <img
                            style={{
                                height: open && !isTabletOrMobile ? '46px' : '27px',
                                transition: '0.5s',
                            }}
                            src={logo}
                            alt="logo"
                        ></img>
                        <Typography variant="body1">{storeData?.name}</Typography>
                    </div>

                    <ContentItems style={{ overflowX: 'hidden', overflowY: 'hidden' }}>
                        <ListItem
                            button
                            className={classes.listItem}
                            selected={homeIsActive}
                            style={{ textDecoration: 'none', marginTop: '10px' }}
                            component={RouterLink}
                            to={routes.homeUrl}
                        >
                            <Tooltip
                                title={open && !isTabletOrMobile ? '' : t('HomeTitle')}
                                placement="right"
                            >
                                <ListItemIcon className={classes.iconMenu}>
                                    <img src={HomeIcon} alt="Home Icon" />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText
                                style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                className={classes.listItemText}
                                primary={t('HomeTitle')}
                            />
                        </ListItem>
                    </ContentItems>

                    <ContentItems
                        style={{ overflowX: 'hidden' }}
                        className={
                            storeDefault && !storeDefault.active ? classes.inactiveForPay : ''
                        }
                    >
                        <List
                            subheader={
                                <ListSubheader
                                    style={{
                                        opacity: open && !isTabletOrMobile ? 1 : 0,
                                    }}
                                    component="div"
                                    classes={{ root: classes.titleSubheader }}
                                >
                                    {t('Menu.myApps')}
                                </ListSubheader>
                            }
                        >
                            <ListItem
                                selected={currentPathName === routes.store}
                                button
                                component={RouterLink}
                                to={routes.store}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('Menu.config')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={EditIcon} alt="EditIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('Menu.config')}
                                />
                            </ListItem>
                            <ListItem
                                selected={currentPathName === routes.userStores}
                                button
                                component={RouterLink}
                                to={routes.userStores}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('Menu.userStores')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={WPIcon} alt="WPIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('Menu.userStores')}
                                />
                            </ListItem>
                        </List>

                        <List
                            subheader={
                                <ListSubheader
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    component="div"
                                    classes={{ root: classes.titleSubheader }}
                                >
                                    Administrar
                                </ListSubheader>
                            }
                        >
                            <ListItem
                                button
                                activeClassName={classes.activeItem}
                                className={classes.listItem}
                                selected={collectionIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.collectionsPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('CollectionsTitle')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={CollectionIcon} alt="CollectionIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('CollectionsTitle')}
                                />
                            </ListItem>
                            <ListItem
                                button
                                className={classes.listItem}
                                selected={productsIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.productsTable}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('ProductsTitle')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={ProductIcon} alt="ProductIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('ProductsTitle')}
                                />
                            </ListItem>
                            <ListItem
                                button
                                className={classes.listItem}
                                selected={couponsIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.couponsPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('CouponsTitle')}
                                    placement="right"
                                >
                                    <Box style={{ position: 'relative', display: 'inline-flex' }}>
                                        <ListItemIcon className={classes.iconMenu}>
                                            <img src={CouponIcon} alt="CouponsIcon" />
                                        </ListItemIcon>
                                        {!couponsFeatureEnabled && <FreemiumCouponNavBadge />}
                                    </Box>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('CouponsTitle')}
                                />
                            </ListItem>
                            <ListItem
                                button
                                className={classes.listItem}
                                selected={promotionsIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.promotionsPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('PromotionsTitle')}
                                    placement="right"
                                >
                                    <Box style={{ position: 'relative', display: 'inline-flex' }}>
                                        <ListItemIcon className={classes.iconMenu}>
                                            <img src={CouponIcon} alt="PromotionsIcon" />
                                        </ListItemIcon>
                                        {promotionsFeatureEnabled ? (
                                            <BetaNavBadge />
                                        ) : (
                                            <FreemiumCouponNavBadge />
                                        )}
                                    </Box>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('PromotionsTitle')}
                                />
                            </ListItem>
                        </List>

                        <List
                            style={{ paddingBottom: 50 }}
                            subheader={
                                <ListSubheader
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    component="div"
                                    classes={{ root: classes.titleSubheader }}
                                >
                                    Desempeño
                                </ListSubheader>
                            }
                        >
                            <ListItem
                                button
                                activeClassName={classes.activeItem}
                                className={classes.listItem}
                                selected={ordersIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.ordersPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('OrdersTitle')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={OrdersIcon} alt="OrdersIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('OrdersTitle')}
                                />
                            </ListItem>

                            <ListItem
                                button
                                activeClassName={classes.activeItem}
                                className={classes.listItem}
                                selected={inventoryIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.inventoryPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('InventoryTitle')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={InventoryIcon} alt="InventoryTitle" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('InventoryTitle')}
                                />
                            </ListItem>

                            <ListItem
                                button
                                className={classes.listItem}
                                selected={clientIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.clientPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('ClientTitle')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={ClientsIcon} alt="ClientsIcon" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={t('ClientTitle')}
                                />
                            </ListItem>

                            <ListItem
                                button
                                className={clsx(classes.listItem, classes.ecometriPlusNavItem)}
                                selected={ecometriPlusIsActive}
                                style={{ textDecoration: 'none' }}
                                component={RouterLink}
                                to={routes.ecometriPlusPage}
                            >
                                <Tooltip
                                    title={open && !isTabletOrMobile ? '' : t('Menu.ecometriPlus')}
                                    placement="right"
                                >
                                    <ListItemIcon className={classes.iconMenu}>
                                        <img src={openInNewIcon} alt="Ecometri AI" />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                    className={classes.listItemText}
                                    primary={
                                        <>
                                            <span className={classes.ecometriPlusLabel}>
                                                {t('Menu.ecometriPlus')}
                                            </span>
                                            <span className={classes.ecometriPlusBadge}>PLUS</span>
                                        </>
                                    }
                                />
                            </ListItem>
                        </List>
                    </ContentItems>

                    <FooterBar>
                        <Divider />
                        <ListItem
                            button
                            activeClassName={classes.activeItem}
                            selected={configIsActive}
                            component={RouterLink}
                            to={routes.config}
                            className={classes.listItem}
                            style={{ textDecoration: 'none' }}
                        >
                            <Tooltip
                                title={open && !isTabletOrMobile ? '' : t('ConfigTitle')}
                                placement="right"
                            >
                                <ListItemIcon className={classes.iconMenu}>
                                    <img src={ConfigIcon} alt="ConfigIcon" />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText
                                style={{ opacity: open && !isTabletOrMobile ? 1 : 0 }}
                                className={classes.listItemText}
                                primary={t('ConfigTitle')}
                            />
                        </ListItem>
                    </FooterBar>
                </Drawer>
            )}
            <main
                className={clsx(classes.content, {
                    [classes.contentShift]: open && !isTabletOrMobile,
                })}
            >
                {notificationMessageStatic && (
                    <InfoNotification isTabletOrMobile={isTabletOrMobile}>
                        <Typography variant="p">
                            {notificationMessageStatic.notification}{' '}
                            <Link component={RouterLink} to={notificationMessageStatic.url}>
                                {notificationMessageStatic.url_label}
                            </Link>
                        </Typography>
                    </InfoNotification>
                )}
                {!notificationMessageStatic && <div className={classes.drawerHeader} />}
                <section className={`${classes.container} `}>
                    <Route
                        exact
                        path={routes.homeUrl}
                        component={
                            isTabletOrMobile
                                ? isStoreHasProduct
                                    ? HomePageMobile
                                    : HomePageMobileNewUsers
                                : HomePage
                        }
                    />
                    <Route exact path={routes.collectionsPage} component={Collections} />
                    <Route exact path={routes.createCollection} component={ManageCollection} />
                    <Route exact path={routes.createOrder} component={CreateOrder} />
                    <Route exact path={routes.createProduct} component={ManageProductPage} />
                    <Route exact path={routes.createUserStore} component={ManageUserStorePage} />
                    <Route
                        exact
                        path={routes.ordersPage}
                        component={isTabletOrMobile ? OrderPageMobile : OrdersTablePage}
                    />
                    <Route
                        exact
                        path={routes.clientPage}
                        component={isTabletOrMobile ? ClientsPageMobile : ClientsPage}
                    />
                    <Route
                        exact
                        path={routes.clientPage + '/:id'}
                        component={isTabletOrMobile ? ClientInfoPageMobile : ClientInfoPage}
                    />
                    <Route
                        exact
                        path={routes.ordersPage + '/:id'}
                        component={isTabletOrMobile ? OrderInfoPageMobile : OrderInfoPage}
                    />
                    <Route
                        exact
                        path={routes.productsTable}
                        component={isTabletOrMobile ? ProductsMobilePage : ProductsTablePage}
                    />
                    <Route exact path={routes.couponsPage} component={CouponsPage} />
                    <Route exact path={routes.ecometriPlusPage} component={EcometriPlusPage} />
                    <Route exact path={routes.promotionsPage} component={PromotionsPage} />
                    <Route exact path={routes.promotionsCreate} component={PromotionFormPage} />
                    <Route
                        exact
                        path={routes.promotionsTierEdit}
                        component={PromotionFormPage}
                    />
                    <Route
                        exact
                        path={routes.promotionsCreateCartTier}
                        component={CartDiscountTierFormPage}
                    />
                    <Route
                        exact
                        path={routes.promotionsCartTierEdit}
                        component={CartDiscountTierFormPage}
                    />
                    {STOREFRONT_PROMOTIONS_ENABLED ? (
                        <Route
                            exact
                            path={routes.promotionsEdit}
                            component={PromotionFormPage}
                        />
                    ) : (
                        <Route
                            exact
                            path={routes.promotionsEdit}
                            render={() => <Redirect to={routes.promotionsPage} />}
                        />
                    )}
                    <Route exact path={routes.store} component={ManageStorePage} />
                    <Route exact path={routes.domain} component={DomainPage} />
                    <Route exact path={routes.form} component={FormPage} />
                    <Route exact path={routes.paymentProviders} component={PaymentProvidersPage} />
                    <Route
                        exact
                        path={routes.addPaymentProviders}
                        component={AddPaymentProvidersPage}
                    />
                    <Route
                        exact
                        path={routes.upgradePaymentProviders}
                        component={AddPaymentProvidersPage}
                    />
                    <Route exact path={routes.qrCode} component={QrPage} />
                    <Route exact path={routes.thankU} component={ThankUPage} />
                    <Route
                        exact
                        path={routes.updateCollection + '/:id'}
                        component={ManageCollection}
                    />
                    <Route
                        exact
                        path={routes.updateProduct + '/:id'}
                        component={ManageProductPage}
                    />
                    <Route
                        exact
                        path={routes.updateUserStore + '/:id'}
                        component={ManageUserStorePage}
                    />
                    <Route
                        exact
                        path={routes.userStores}
                        component={
                            isTabletOrMobile ? MobileUserStoresTablePage : UserStoresTablePage
                        }
                    />
                    <Route exact path={routes.config} component={ConfigPage} />
                    <Route exact path={routes.billing} component={BillingPage} />
                    <Route exact path={routes.bill + '/:id'} component={BillDetailPage} />
                    <Route
                        exact
                        path={routes.payments + '/:planId/:paymentTypeId'}
                        component={PaymentPage}
                    />
                    <Route exact path={routes.payBill + '/:id'} component={BillPaymentPage} />
                    <Route
                        exact
                        path={routes.inventoryPage}
                        component={isTabletOrMobile ? MobileInventoryPage : InventoryPage}
                    />
                    <Route
                        exact
                        path={routes.inventoryImportPage}
                        component={InventoryImportPage}
                    />
                    <Route exact path={routes.importProducts} component={ImportProductsPage} />
                    <Route exact path={routes.massiveUploadTable} component={MassiveUploadTable} />
                    <Route exact path={routes.general} component={GeneralPage} />
                    <Route exact path={routes.legal} component={LegalPage} />
                    <Route exact path={routes.shipping} component={ShippingConfigPage} />
                    <Route exact path={routes.usersRoles} component={UsersRolesPage} />
                    <Route exact path={routes.createUserRole} component={ManageUserRole} />
                    <Route exact path={routes.updateUserRole + '/:id'} component={ManageUserRole} />
                    <Route
                        exact
                        path={routes.mobileManageCategories}
                        component={ActionCategorieMobile}
                    />
                    <Route exact path={routes.downloadReportPage} component={DownloadReportPage} />
                    <Route exact path={routes.editStore} component={EditStore} />
                    {isTabletOrMobile && (
                        <FooterMenuBar onOpenSettingsMenu={toggleDrawer} />
                    )}
                </section>
            </main>
            {storeNameDefault && (
                <PreviewLinkStore
                    open={cancelDialogOpen}
                    dataStore={storeDefault}
                    storeId={storeId}
                    onClose={() => setCancelDialogOpen(false)}
                    title={t('PreviewLink.title')}
                    message={t('PreviewLink.subtitle')}
                />
            )}
            <WhatsAppWidget
                phoneNumber="17543296020"
                textReplyTime={false}
                message="¡Hola! 👋🏼  ¿En qué te puedo ayudar?"
                companyName="Soporte ecometri"
                placeholder="Escribe un mensaje"
                sendButton="Enviar"
            />
            <WizardSelectPlan
                open={selectPlanDialogOpen}
                currentPlan={infoStore}
                storeId={storeId}
                onClose={() => setSelectPlanDialogOpen(false)}
                logOut={logOut}
                disableCloseBtn
                disableBackdropClick
                disableEscapeKeyDown
            />
            {/* TODO: don't delete, disable popup*/}
            {/* <PopupPayAnnualDiscount open={showWelcomePopup} /> */}
            <PopupFinishTrialPlan open={showPlanDialogOpen} disableEscapeKeyDown />
            <AddProductModal
                openAddProduct={openAddProductModal}
                onAddProductClick={onAddProductClick}
                disableEscapeKeyDown
            />

            <SubcategoriesFeatureVideoModal
                open={showSubcategoriesVideoModal}
                onClose={dismissSubcategoriesVideoModal}
            />

            <Dialog
                open={showFreemiumProductsModal}
                onClose={() => setShowFreemiumProductsModal(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        padding: '16px 12px',
                        borderRadius: 16,
                    },
                }}
            >
                <DialogTitle disableTypography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <ErrorOutlineIcon style={{ color: '#D32F2F', marginRight: 8 }} />
                            <Typography variant="h6" style={{ color: '#D32F2F' }}>
                                Tu plan Freemium tiene un límite de 20 productos
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={() => setShowFreemiumProductsModal(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                        Estamos actualizando nuestras políticas. Tu tienda está en el plan Freemium y
                        tiene un límite de <strong>20 productos publicados</strong>.
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        Si quieres conservar todos tus productos actuales y seguir agregando más sin
                        límite, necesitas actualizar a un plan de pago. (El plan Pro permite
                        tener hasta <strong>1.000 productos</strong>.)
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        Si prefieres no mejorar tu plan ahora o tienes dudas, puedes ponerte en contacto
                        con nuestro equipo escribiendo a{' '}
                        <Link href="mailto:comercial@ecometri.com" color="primary">
                            comercial@ecometri.com
                        </Link>{' '}
                        o por WhatsApp a{' '}
                        <Link
                            href={`https://wa.me/17543296020?text=${encodeURIComponent(
                                'Hola, tengo una tienda en el plan Freemium y me gustaría hablar sobre el límite de 20 productos y las opciones de plan Pro (hasta 1.000 productos).'
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                        >
                            +1 754 329 6020
                        </Link>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const supportNumber = '17543296020';
                            const message =
                                'Hola, tengo una tienda en el plan Freemium y me gustaría hablar sobre el límite de 20 productos y otras opciones de plan.';
                            window.open(
                                `https://wa.me/${supportNumber}?text=${encodeURIComponent(message)}`,
                                '_blank',
                                'noopener,noreferrer'
                            );
                        }}
                        color="default"
                    >
                        Hablar con soporte
                    </Button>
                    <ButtonUpgradePlan size="small" />
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    isLoggingOut: auth.isUserAuthenticated,
    userEmail: auth.userEmail,
    userType: auth.userType,
    storeData: common.storeData,
    showWelcomePopup: common.showWelcomePopup,
    auth,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            logOut,
            setStoreData,
            setIsPlanNearExpiry,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
